<template>
	<v-dialog
		v-model="$props.dialogVisible"
		:max-width="$props.options?.width"
		:width="$vuetify.breakpoint.smAndDown ? '90%' : '80%'"
		:style="{ zIndex: 10000 }"
		:persistent="$props.persistentDialog"
		@keydown.esc="(customOutsideClickFunction || cancel)()"
		@click:outside="(customOutsideClickFunction || cancel)()"
		@keydown.enter.prevent>
		<v-card class="pa-2" v-test="'dialog'">
			<v-card-title
				class="text-h5 pa-4 dialog-header"
				:style="{ 'word-break': 'normal' }"
				:class="{ 'shadow-bottom': hasScrolledDown }">
				<h5 v-test="'dialog-title'" id="dialog-title">{{ $props.title }}</h5>
				<slot name="dialog-header">
					<!-- for more complex displays -->
				</slot>
			</v-card-title>
			<div
				class="dialog-body-container"
				:class="$vuetify.breakpoint.smAndDown && isLandscape ? 'landscape-dialog-body-container' : ''"
				ref="dialogBodyContainer"
				@scroll="handleScroll">
				<v-card-text class="pa-4" v-test="'dialog-message'">
					<span v-html="message"></span>
					<slot name="dialog-body">
						<!-- for more complex displays -->
					</slot>
				</v-card-text>
			</div>
			<v-card-actions
				class="pa-4 d-flex align-center flex-wrap ga-2 dialog-footer"
				:class="{
					'shadow-top': hasScrolledUp,
					'justify-space-between': $props.customButton,
					'justify-end': !$props.customButton,
				}">
				<template>
					<v-btn
						v-if="!$props.removeCancelButton"
						id="cancel-action-btn"
						:class="$props.primaryCancelButton ? '' : 'white-bg'"
						rounded
						elevation="0"
						:color="$props.primaryCancelButton ? 'primary' : 'grey'"
						:text="$props.primaryCancelButton ? false : true"
						@click.native="cancel"
						v-test="'dialog-cancel-button'"
						v-track="'dialog-cancel-button'">
						<v-icon v-if="$props.customCancelIcon" size="20">{{ customCancelIcon }}</v-icon>
						{{ $props.cancelText }}
					</v-btn>
					<v-btn
						v-if="!$props.removeAgreeButton"
						rounded
						elevation="0"
						id="confirm-action-btn"
						:class="$props.isAngry ? 'delete-btn' : ''"
						color="primary"
						class="flex-grow-0 flex-shrink-0"
						@click.native="agree"
						:disabled="$props.disableAgreeButton"
						v-test="'dialog-agree-button'"
						v-track="'dialog-agree-button'">
						<v-icon v-if="$props.customAgreeIcon" size="20">{{ customAgreeIcon }}</v-icon>
						{{ $props.agreeText }}
					</v-btn>
					<v-btn
						v-if="$props.customButton"
						id="custom-action-btn"
						color="primary"
						class="flex-grow-0 flex-shrink-0"
						rounded
						elevation="0"
						:disabled="$props.disableCustomButton"
						@click.native="customAction"
						v-test="'dialog-custom-button'"
						v-track="'dialog-custom-button'">
						{{ $props.customText }}
					</v-btn>
				</template>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts" setup>
	import { ref, defineProps, defineEmits, nextTick, onMounted, onBeforeUnmount, watch } from "vue";
	import { blurActiveElement } from "@/helpers/utilities";

	let hasScrolledDown = ref<boolean>(false);
	let hasScrolledUp = ref<boolean>(false);
	const dialogBodyContainer = ref<HTMLElement | null>(null);
	const isLandscape = ref(false);

	const props = defineProps({
		dialogVisible: {
			type: Boolean,
			default: false,
			required: true,
		},
		title: {
			type: String,
		},
		message: {
			type: String,
		},
		options: {
			type: Object,
			default: () => ({
				width: "500px",
			}),
		},
		isAngry: {
			type: Boolean,
			default: false,
		},
		removeCancelButton: {
			type: Boolean,
			default: false,
		},
		removeAgreeButton: {
			type: Boolean,
			default: false,
		},
		agreeText: {
			type: String,
			default: "OK",
		},
		cancelText: {
			type: String,
			default: "Cancel",
		},
		disableAgreeButton: {
			type: Boolean,
			default: false,
		},
		persistentDialog: {
			type: Boolean,
			default: false,
		},
		customButton: {
			type: Boolean,
			default: false,
		},
		customText: {
			type: String,
			default: "Save As",
		},
		disableCustomButton: {
			type: Boolean,
			default: false,
		},
		customOutsideClickFunction: {
			type: Function,
			default: null,
		},
		primaryCancelButton: {
			type: Boolean,
			default: false,
		},
		customCancelIcon: {
			type: String,
			default: "",
		},
		customAgreeIcon: {
			type: String,
			default: "",
		},
	});
	const emit = defineEmits(["cancel", "agree", "update", "customAction"]);

	const cancel = () => {
		emit("cancel");
		nextTick(() => {
			blurActiveElement();
		});
	};
	const agree = () => {
		emit("agree");
	};
	const customAction = () => {
		emit("customAction");
	};
	function handleScroll(event: Event) {
		const element = event.target as HTMLElement;
		if (!element) return;
		hasScrolledDown.value = element.scrollTop > 0;
		hasScrolledUp.value = element.scrollTop + element.clientHeight < element.scrollHeight;
	}

	const updateOrientation = () => {
		if (screen.orientation) {
			isLandscape.value = screen.orientation.type.startsWith("landscape");
		}
	};

	onMounted(() => {
		updateOrientation();
		if (screen.orientation) {
			screen.orientation.addEventListener("change", updateOrientation);
		}
	});

	onBeforeUnmount(() => {
		if (screen.orientation) {
			screen.orientation.removeEventListener("change", updateOrientation);
		}
	});

	watch(
		() => props.dialogVisible,
		async (newVal) => {
			if (newVal && dialogBodyContainer.value) {
				await nextTick();
				dialogBodyContainer.value.scrollTop = 0;
				hasScrolledDown.value = false;
				hasScrolledUp.value = false;
			}
		}
	);
</script>

<style css scoped>
	.v-btn.primary.delete-btn {
		background-color: var(--v-error-base) !important;
		color: #fff !important;
	}
	.dialog-header,
	.dialog-footer {
		background-color: #fff;
	}

	.dialog-header.shadow-bottom {
		box-shadow: 0 6px 6px -6px rgba(0, 0, 0, 0.2);
	}
	.dialog-footer.shadow-top {
		box-shadow: 0 -6px 6px -6px rgba(0, 0, 0, 0.2);
	}

	.dialog-body-container {
		max-height: 50vh !important;
		overflow-y: auto;
		scroll-behavior: smooth;
		color: #00000099 !important;
	}

	.landscape-dialog-body-container {
		max-height: 40vh !important;
	}

	.v-btn {
		text-transform: none !important;
	}
</style>
